import React, { useEffect, useState, useCallback } from 'react';
import api from '../../api';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { PasswordInput } from '../../components/ui/password-input';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter, DialogTrigger
} from '../../components/ui/dialog';
import { toast } from 'sonner';
import { KeyRound, Trash2, RefreshCw, Users as UsersIcon, Copy, Search } from 'lucide-react';

export default function AdminUsers() {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');

  const load = useCallback(() => {
    setLoading(true);
    api.getAdminUsers()
      .then(setUsers)
      .catch(e => toast.error(e.message || 'Erreur'))
      .finally(() => setLoading(false));
  }, []);

  useEffect(() => { load(); }, [load]);

  const roleTag = (role) => {
    if (role === 'admin') return 'mc-tag mc-tag-red';
    if (role === 'enseignant') return 'mc-tag mc-tag-amber';
    return 'mc-tag mc-tag-blue';
  };

  const roleLabel = (role) => {
    if (role === 'admin') return 'Admin';
    if (role === 'enseignant') return 'Enseignant';
    return 'Élève';
  };

  const filtered = users.filter(u => {
    if (!search.trim()) return true;
    const q = search.toLowerCase();
    return (u.email || '').toLowerCase().includes(q) ||
           (u.name || '').toLowerCase().includes(q) ||
           (u.role || '').toLowerCase().includes(q);
  });

  return (
    <div className="space-y-5 max-w-5xl" data-testid="admin-users-page">
      <div className="flex items-start justify-between flex-wrap gap-3">
        <div>
          <div className="inline-flex items-center gap-1.5 text-[10px] font-black uppercase tracking-widest text-[hsl(var(--primary))] mb-1">
            <UsersIcon className="w-3.5 h-3.5" /> Back-office admin
          </div>
          <h1 className="text-2xl sm:text-3xl font-black tracking-tight">Utilisateurs</h1>
          <p className="text-sm text-muted-foreground mt-1">
            Liste complète des comptes. Vous pouvez réinitialiser un mot de passe ou supprimer un compte.
          </p>
        </div>
        <Button variant="outline" size="sm" className="rounded-full gap-1.5" onClick={load} data-testid="users-refresh-btn">
          <RefreshCw className="w-4 h-4" /> Rafraîchir
        </Button>
      </div>

      {/* Recherche */}
      <div className="mc-card p-3">
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
          <Input
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Rechercher par email, nom ou rôle..."
            className="pl-9"
            data-testid="users-search-input"
          />
        </div>
        <div className="text-xs text-muted-foreground mt-2">
          {filtered.length} utilisateur{filtered.length > 1 ? 's' : ''}{search ? ` (sur ${users.length} au total)` : ''}
        </div>
      </div>

      <div className="mc-card overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>Nom</TableHead>
              <TableHead>Email</TableHead>
              <TableHead>Rôle</TableHead>
              <TableHead>Premium</TableHead>
              <TableHead className="text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading && <TableRow><TableCell colSpan={5} className="text-center text-muted-foreground py-6">Chargement...</TableCell></TableRow>}
            {!loading && filtered.length === 0 && (
              <TableRow><TableCell colSpan={5} className="text-center text-muted-foreground py-6">Aucun utilisateur.</TableCell></TableRow>
            )}
            {filtered.map((u, i) => (
              <TableRow key={u.email || i} data-testid={`admin-user-row-${i}`}>
                <TableCell className="font-semibold">{u.name}</TableCell>
                <TableCell className="break-all">{u.email}</TableCell>
                <TableCell><span className={roleTag(u.role)}>{roleLabel(u.role)}</span></TableCell>
                <TableCell>{u.is_premium ? '✓' : '—'}</TableCell>
                <TableCell className="text-right">
                  <div className="flex gap-1.5 justify-end flex-wrap">
                    <ResetPasswordDialog user={u} onDone={load} />
                    {u.role !== 'admin' && <DeleteUserDialog user={u} onDone={load} />}
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>
    </div>
  );
}

// ==========================
// Reset Password Dialog
// ==========================
function generateTempPassword() {
  const chars = 'ABCDEFGHJKMNPQRSTUVWXYZ23456789';
  let pwd = '';
  for (let i = 0; i < 8; i++) pwd += chars.charAt(Math.floor(Math.random() * chars.length));
  return pwd + '!';
}

function ResetPasswordDialog({ user, onDone }) {
  const [open, setOpen] = useState(false);
  const [password, setPassword] = useState('');
  const [busy, setBusy] = useState(false);
  const [confirmed, setConfirmed] = useState(false);

  const handleGenerate = () => setPassword(generateTempPassword());

  const handleReset = async () => {
    if (password.length < 6) {
      toast.error('Le mot de passe doit faire au moins 6 caractères');
      return;
    }
    setBusy(true);
    try {
      await api.resetUserPassword(user.email, password);
      toast.success(`Mot de passe réinitialisé pour ${user.email}`);
      setConfirmed(true);
      onDone?.();
    } catch (e) {
      toast.error(e.message || 'Erreur');
    } finally {
      setBusy(false);
    }
  };

  const handleCopy = async () => {
    try {
      await navigator.clipboard.writeText(password);
      toast.success('Mot de passe copié !');
    } catch (e) { console.warn('[Users] clipboard write failed:', e); }
  };

  const handleClose = (isOpen) => {
    setOpen(isOpen);
    if (!isOpen) {
      setPassword('');
      setConfirmed(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogTrigger asChild>
        <Button
          variant="outline"
          size="sm"
          className="rounded-full gap-1.5 h-8 text-xs"
          data-testid={`user-reset-password-${user.email}`}
        >
          <KeyRound className="w-3.5 h-3.5" /> Réinitialiser
        </Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Réinitialiser le mot de passe</DialogTitle>
          <DialogDescription>
            Définissez un nouveau mot de passe pour <strong>{user.name}</strong> ({user.email}).
            Pensez à communiquer ce mot de passe à l'utilisateur de façon sécurisée
            (SMS, téléphone, messagerie chiffrée).
          </DialogDescription>
        </DialogHeader>

        {!confirmed ? (
          <div className="space-y-3">
            <div>
              <Label htmlFor="new-password">Nouveau mot de passe (6 caractères min.)</Label>
              <PasswordInput
                id="new-password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Saisir ou générer..."
                minLength={6}
                data-testid="user-new-password-input"
              />
            </div>
            <Button
              type="button"
              variant="outline"
              size="sm"
              onClick={handleGenerate}
              className="rounded-full gap-1.5 w-full"
              data-testid="user-generate-password"
            >
              <RefreshCw className="w-3.5 h-3.5" /> Générer un mot de passe aléatoire
            </Button>
          </div>
        ) : (
          <div className="rounded-xl bg-emerald-50 dark:bg-emerald-950/40 border border-emerald-200 dark:border-emerald-900/50 p-3">
            <div className="text-[10px] font-black uppercase tracking-widest text-emerald-700 dark:text-emerald-300 mb-1">
              Mot de passe réinitialisé
            </div>
            <p className="text-xs text-foreground/85 mb-2">
              Transmettez ce mot de passe à <strong>{user.email}</strong> :
            </p>
            <div className="flex items-center gap-2 rounded-lg bg-white dark:bg-black/30 border border-border p-2 font-mono text-sm">
              <span className="flex-1 break-all select-all">{password}</span>
              <Button size="sm" variant="ghost" className="h-7 px-2 flex-shrink-0" onClick={handleCopy} data-testid="user-copy-password">
                <Copy className="w-3.5 h-3.5" />
              </Button>
            </div>
          </div>
        )}

        <DialogFooter>
          <Button variant="ghost" onClick={() => handleClose(false)} disabled={busy}>
            {confirmed ? 'Fermer' : 'Annuler'}
          </Button>
          {!confirmed && (
            <Button
              onClick={handleReset}
              disabled={busy || password.length < 6}
              className="mc-btn-primary"
              data-testid="user-confirm-reset"
            >
              Confirmer la réinitialisation
            </Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

// ==========================
// Delete User Dialog
// ==========================
function DeleteUserDialog({ user, onDone }) {
  const [open, setOpen] = useState(false);
  const [confirmText, setConfirmText] = useState('');
  const [busy, setBusy] = useState(false);

  const handleDelete = async () => {
    if (confirmText !== user.email) {
      toast.error("La confirmation ne correspond pas");
      return;
    }
    setBusy(true);
    try {
      await api.deleteUser(user.email);
      toast.success(`Compte ${user.email} supprimé`);
      setOpen(false);
      setConfirmText('');
      onDone?.();
    } catch (e) {
      toast.error(e.message || 'Erreur');
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button
          variant="outline"
          size="sm"
          className="rounded-full gap-1.5 h-8 text-xs border-rose-300 text-rose-700 dark:text-rose-300 dark:border-rose-900/50 hover:bg-rose-50 dark:hover:bg-rose-950/30"
          data-testid={`user-delete-${user.email}`}
        >
          <Trash2 className="w-3.5 h-3.5" /> Supprimer
        </Button>
      </DialogTrigger>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Supprimer ce compte ?</DialogTitle>
          <DialogDescription>
            Cette action est <strong>irréversible</strong>. Le compte de <strong>{user.name}</strong>
            ({user.email}) ainsi que toute sa progression (QCM, favoris, modules vus) seront
            définitivement effacés.
          </DialogDescription>
        </DialogHeader>
        <div>
          <Label htmlFor="confirm-email">Pour confirmer, saisissez l'email : <code className="font-mono text-xs">{user.email}</code></Label>
          <Input
            id="confirm-email"
            value={confirmText}
            onChange={(e) => setConfirmText(e.target.value)}
            placeholder={user.email}
            data-testid="user-delete-confirm-input"
          />
        </div>
        <DialogFooter>
          <Button variant="ghost" onClick={() => setOpen(false)} disabled={busy}>Annuler</Button>
          <Button
            onClick={handleDelete}
            disabled={busy || confirmText !== user.email}
            className="bg-rose-600 hover:bg-rose-700 text-white"
            data-testid="user-confirm-delete"
          >
            Supprimer définitivement
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
